
import React, { useState, useEffect } from 'react';
import { Flame, ArrowRight, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import FaitImage from './FaitImage';
import FadeIn from './FadeIn';
import { useLanguage } from './LanguageContext';
import SEO from './SEO';

type Category = 'all' | 'mains' | 'sides' | 'drinks';

const Menu: React.FC = () => {
    const { t, isRtl, language } = useLanguage();
    const ArrowIcon = isRtl ? ArrowLeft : ArrowRight;
    const [activeCategory, setActiveCategory] = useState<Category>('all');
    const [isAnimating, setIsAnimating] = useState(false);

    // Images mapping - Using ONLY existing public images
    const getProductImage = (id: string) => {
        switch(id) {
            case '1': return '/images/hero-cholent.jpg'; // Cholent
            case '2': return '/images/cholent-plate.jpg'; // Kishke (reuse plate)
            case '3': return '/images/kugel-yerushalmi.jpg'; // Kugel Yerushalmi
            case '4': return '/images/kugel-yerushalmi.jpg'; // Potato Kugel (reuse yerushalmi)
            case '5': return '/images/atmosphere-interior.jpg'; // Drinks (reuse interior)
            default: return '/images/hero-cholent.jpg';
        }
    };

    // Filter Logic
    const filteredItems = activeCategory === 'all' 
        ? t.menu.items 
        : t.menu.items.filter(item => item.category === activeCategory);

    const handleCategoryChange = (cat: Category) => {
        if (cat === activeCategory) return;
        setIsAnimating(true);
        setActiveCategory(cat);
        setTimeout(() => setIsAnimating(false), 300);
    };

    const categories: { id: Category; label: string }[] = [
        { id: 'all', label: t.menu.categories.all },
        { id: 'mains', label: t.menu.categories.mains },
        { id: 'sides', label: t.menu.categories.sides },
        { id: 'drinks', label: t.menu.categories.drinks },
    ];

    // Constructing Schema.org Menu Data
    const menuSchema = {
      "@type": "Menu",
      "name": language === 'he' ? "תפריט יהודה פייט" : "Yehuda Fait Menu",
      "hasMenuSection": [
        {
          "@type": "MenuSection",
          "name": language === 'he' ? "עיקריות" : "Main Dishes",
          "hasMenuItem": t.menu.items.map(item => ({
             "@type": "MenuItem",
             "name": item.name,
             "description": item.description,
             "offers": {
               "@type": "Offer",
               "price": item.price ? item.price.replace(/[^0-9]/g, '') : "0",
               "priceCurrency": "ILS"
             }
          }))
        }
      ]
    };

  return (
    <section className="py-24 bg-[#120C08] min-h-screen relative">
      <SEO 
        title={language === 'he' ? 'התפריט' : 'Menu'} 
        description={t.menu.description}
        path="/menu"
        schema={menuSchema}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        <div className="text-center mb-12 pt-12">
          <FadeIn direction="down">
            <span className="text-fait-gold font-sans text-sm tracking-widest uppercase mb-2 block">{t.menu.title}</span>
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-fait-gold drop-shadow-md">{t.menu.subtitle}</h2>
          </FadeIn>
          <FadeIn delay={200}>
            <p className="mt-4 text-gray-300 max-w-lg mx-auto font-sans font-light">
                {t.menu.description}
            </p>
          </FadeIn>
        </div>

        {/* Tab Navigation */}
        <FadeIn delay={300} className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((cat) => (
                <button
                    key={cat.id}
                    onClick={() => handleCategoryChange(cat.id)}
                    className={`px-6 py-2 rounded-full font-serif text-lg transition-all duration-300 border ${
                        activeCategory === cat.id 
                            ? 'bg-fait-gold text-fait-dark border-fait-gold font-bold shadow-[0_0_15px_rgba(206,172,100,0.4)]' 
                            : 'bg-transparent text-gray-400 border-white/10 hover:border-fait-gold/50 hover:text-white'
                    }`}
                >
                    {cat.label}
                </button>
            ))}
        </FadeIn>

        {/* Grid Layout */}
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 transition-opacity duration-300 ${isAnimating ? 'opacity-0' : 'opacity-100'}`}>
          {filteredItems.map((item, index) => (
            <FadeIn key={item.id} delay={100} className="h-full">
              <Link 
                to={`/menu/${item.id}`}
                className="relative overflow-hidden rounded-3xl group shadow-2xl bg-[#1E1611] border border-fait-brown/20 hover:border-fait-gold/40 transition-all duration-500 flex flex-col h-full hover:-translate-y-2 block"
              >
                {/* Floating Image Container - No dark overlay over the whole image */}
                <div className="p-5 pb-0 w-full shrink-0">
                    <div className="h-60 w-full relative overflow-hidden rounded-2xl shadow-lg group-hover:shadow-amber-900/20 transition-all duration-500">
                        {/* Gradient only at the bottom for image text contrast if needed, mostly clear */}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#1E1611]/40 via-transparent to-transparent z-10"></div>
                        
                        <FaitImage 
                          src={getProductImage(item.id)} 
                          alt={item.name}
                          imageContext={item.imageContext as any}
                          className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                        />
                        
                        {/* Badge for Best Seller */}
                        {item.bestSeller && (
                          <div className="absolute top-3 right-3 rtl:right-3 rtl:left-auto ltr:left-3 ltr:right-auto z-20 bg-fait-gold text-fait-dark font-bold px-3 py-1 rounded-full text-xs shadow-lg flex items-center gap-1">
                              <Flame size={12} fill="currentColor" />
                              {t.menu.bestSellerLabel}
                          </div>
                        )}
                    </div>
                </div>

                {/* Content - Brighter text for contrast against dark card bg */}
                <div className="relative z-20 p-6 flex flex-col flex-grow">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="text-2xl font-serif font-bold text-white group-hover:text-fait-gold transition-colors">
                          {item.name}
                      </h3>
                      {item.isSpicy && (
                           <div className="bg-red-500/20 p-1.5 rounded-full" title="Hot">
                              <Flame className="text-red-500 w-4 h-4 animate-pulse" />
                           </div>
                      )}
                    </div>
                    
                    {/* Updated text color to gray-200/300 for readability */}
                    <p className="text-gray-300 font-sans text-base leading-relaxed mb-6 border-r-2 rtl:border-r-2 rtl:border-l-0 ltr:border-l-2 ltr:border-r-0 border-fait-brown/50 pr-3 rtl:pr-3 rtl:pl-0 ltr:pl-3 ltr:pr-0 flex-grow">
                        {item.description}
                    </p>

                    <div className="mt-auto pt-4 border-t border-white/10 flex justify-between items-center">
                        {item.price ? (
                            <span className="text-fait-gold font-bold text-xl">{item.price}</span>
                        ) : (
                            <span className="text-gray-500 text-sm italic">{t.menu.recommendedAddon}</span>
                        )}
                        
                        {/* Decorative link icon */}
                        <span className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-fait-gold group-hover:text-fait-dark transition-colors">
                            <ArrowIcon size={16} />
                        </span>
                    </div>
                </div>
              </Link>
            </FadeIn>
          ))}
        </div>

        {/* Empty State */}
        {filteredItems.length === 0 && (
            <div className="text-center py-20 text-gray-500">
                <p>לא נמצאו פריטים בקטגוריה זו</p>
            </div>
        )}

        <FadeIn delay={200} className="mt-20 text-center">
            <Link to="/contact" className="inline-flex items-center gap-2 text-fait-gold hover:text-white border-b border-fait-gold pb-1 transition-colors font-serif italic text-xl">
                {t.menu.cta} <span className="not-italic"><ArrowIcon size={18} /></span>
            </Link>
        </FadeIn>

      </div>
    </section>
  );
};

export default Menu;
